import {
  useActiveScenario,
  useScenarioStore,
  type Scenario,
  type ScenarioArea,
} from "../../state/useScenarioStore";
import { useAreaScenario } from "../../lib/useScenario";
import { euroShort, pct } from "../../lib/format";

/**
 * Every saved scenario, priced against whatever area is currently selected.
 *
 * This is the point of keeping several: clicking one neighbourhood answers
 * "60 m² over 10 years", "110 m² over 20 at a flat 3%" and everything else you
 * have set up, all at once — rather than making you switch between them and
 * hold the previous number in your head.
 *
 * With nothing selected each row falls back to its own first area, so the list
 * still says what each scenario is about.
 */
export function ScenarioList({ area }: { area: ScenarioArea | null }) {
  const scenarios = useScenarioStore((s) => s.scenarios);
  const active = useActiveScenario();
  const create = useScenarioStore((s) => s.create);

  return (
    <section>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h3 className="min-w-0 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
          Scenarios
          {area && (
            <span className="normal-case tracking-normal">
              {" "}
              &mdash; <span className="text-ink">{area.name}</span>
            </span>
          )}
        </h3>
        <button
          onClick={create}
          className="shrink-0 rounded-md border border-line px-1.5 py-0.5 text-[10px] font-medium text-ink-soft hover:border-accent hover:text-accent"
        >
          + New
        </button>
      </div>
      <ul className="space-y-1.5">
        {scenarios.map((s) => (
          <Row
            key={s.id}
            scenario={s}
            active={s.id === active.id}
            area={area}
          />
        ))}
      </ul>
    </section>
  );
}

function Row({
  scenario,
  active,
  area,
}: {
  scenario: Scenario;
  active: boolean;
  area: ScenarioArea | null;
}) {
  const activate = useScenarioStore((s) => s.activate);
  const rename = useScenarioStore((s) => s.rename);
  const duplicate = useScenarioStore((s) => s.duplicate);
  const remove = useScenarioStore((s) => s.remove);
  const count = useScenarioStore((s) => s.scenarios.length);

  const summary = [
    `${scenario.surface} m²`,
    `${scenario.horizon} yr`,
    scenario.growthMode === "historic"
      ? "own trend"
      : `${pct(scenario.customRate)}/yr`,
  ].join(" · ");

  const body = (
    <>
      <div className="text-[10px] tabular-nums text-ink-soft">{summary}</div>
      <Figures scenario={scenario} area={area} />
    </>
  );

  return (
    <li
      className={`relative rounded-lg border transition-colors ${
        active ? "border-accent bg-surface-alt" : "border-line hover:bg-surface-alt"
      }`}
    >
      {active ? (
        <div className="px-2.5 py-1.5 pr-14">
          {/* Editable in place: renaming the thing you are already editing
              should not need a mode of its own. */}
          <input
            value={scenario.name}
            onChange={(e) => rename(scenario.id, e.target.value)}
            aria-label="Scenario name"
            className="-mx-1 w-full rounded border border-transparent bg-transparent px-1 text-[12px] font-semibold outline-none hover:border-line focus:border-accent focus:bg-surface"
          />
          {body}
        </div>
      ) : (
        <button
          onClick={() => activate(scenario.id)}
          className="block w-full px-2.5 py-1.5 pr-14 text-left"
        >
          <span className="block truncate text-[12px] font-medium">
            {scenario.name}
          </span>
          {body}
        </button>
      )}

      <div className="absolute right-0.5 top-0.5 flex gap-0.5">
        <button
          onClick={() => duplicate(scenario.id)}
          className="flex h-5 w-5 items-center justify-center rounded text-[11px] leading-none text-ink-soft hover:bg-surface hover:text-accent"
          title="Duplicate this scenario"
          aria-label={`Duplicate ${scenario.name}`}
        >
          ⧉
        </button>
        <button
          onClick={() => remove(scenario.id)}
          disabled={count === 1}
          className="flex h-5 w-5 items-center justify-center rounded text-[11px] leading-none text-ink-soft hover:bg-surface hover:text-[#cf3a2e] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-soft"
          title={count === 1 ? "The last scenario cannot be removed" : "Delete"}
          aria-label={`Delete ${scenario.name}`}
        >
          ✕
        </button>
      </div>
    </li>
  );
}

/** What this scenario says the target area is worth, now and at its horizon. */
function Figures({
  scenario,
  area,
}: {
  scenario: Scenario;
  area: ScenarioArea | null;
}) {
  // The selected area wins: the whole list is answering a question about it.
  const target = area ?? scenario.areas[0] ?? null;
  const { projection, isLoading } = useAreaScenario(target, scenario);

  if (!target) return <div className="text-[10px] text-ink-soft">No area yet</div>;

  const saved = scenario.areas.some(
    (a) => a.scope === target.scope && a.code === target.code,
  );
  const change =
    projection && projection.base.value > 0
      ? (projection.final - projection.base.value) / projection.base.value
      : null;
  const up = change !== null && change >= 0;

  return (
    <div className="flex items-baseline gap-1.5 text-[10px]">
      {saved && (
        <span
          className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-accent"
          title={`${target.name} is saved in ${scenario.name}`}
        />
      )}
      {/* Only worth naming when it is the scenario's own area — when the list
          is pricing the selection, every row is about the same place and the
          heading already says which. */}
      {!area && (
        <span className="min-w-0 flex-1 truncate text-ink-soft">{target.name}</span>
      )}
      {isLoading ? (
        <span className="text-ink-soft">…</span>
      ) : !projection ? (
        <span className="text-ink-soft">no median here</span>
      ) : (
        <>
          <span className="shrink-0 whitespace-nowrap tabular-nums">
            {euroShort(projection.base.value)}
            <span className="text-ink-soft"> → </span>
            <span className={up ? "text-[#2e7d32]" : "text-[#cf3a2e]"}>
              {euroShort(projection.final)}
            </span>
          </span>
          {change !== null && (
            <span
              className={`shrink-0 tabular-nums ${up ? "text-[#2e7d32]" : "text-[#cf3a2e]"}`}
            >
              {pct(change)}
            </span>
          )}
        </>
      )}
    </div>
  );
}
